/**
 * 配送地址选择抽屉。
 * 从 OrderInfo 迁出：Header / 商详 / 结算共用，避免 NavBar 同步引用 pages 把结算页样式打进首屏 CSS。
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Input, Tag, Skeleton, message } from 'antd';
import { useSelector } from 'react-redux';
import { filterParams } from '@/util';
import { TeamOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getAddressList, getPostalCodeAddress, GetAddressListType } from '@/api/newApi/personalCenter/address';
import styles from './index.module.scss';
import roleIcon from '@/assets/images/orderInfo/role.png';
import addIcon from '@/assets/images/madd.png';
import closeIcon from '@/assets/images/mclose.png';
import locationIcon from '@/assets/images/mlocation.png';
import searchIcon from '@/assets/images/msearch.png';
import selectIcon from '@/assets/images/mselect.png';
import { normalizePostalCodeAddress, PostalCodeAddress } from '@/util/postalCodeAddress';

type PropApi = {
  closeDrawer?: (item?: any) => void; // 关闭抽屉回调
  showDrawer: boolean; // 显示抽屉
  consigneeList: any[]; // 其他地址列表
  consignee: any; // 当前地址
  setConsigneeList: any;
  setConsignee: any;
  showAddAddressButton?: boolean;
  onAddAddressClick?: () => void;
  showPostalCodeLookup?: boolean;
  initialPostalCode?: string;
  onPostalLocationSelect?: (location: PostalCodeAddress) => void | Promise<void>;
};

export default (props: PropApi) => {
  const {
    closeDrawer,
    showDrawer,
    consigneeList,
    consignee = null,
    setConsigneeList,
    setConsignee,
    showAddAddressButton = false,
    onAddAddressClick,
    showPostalCodeLookup = false,
    initialPostalCode = '',
    onPostalLocationSelect,
  } = props;
  const [open, setOpen] = useState(false);
  const [selectConsignee, setSelectConsignee] = useState<any>({}); // 当前选择的地址
  const [currentSearchWord, setCurrentSearchWord] = useState<string>(''); // 当前搜索词
  const [addressList, setAddressList] = useState<any[]>([]); // 从API获取的地址列表
  const [loading, setLoading] = useState(false); // 加载状态
  const [postalCode, setPostalCode] = useState(initialPostalCode);
  const [postalLookupLoading, setPostalLookupLoading] = useState(false);
  const [postalLookupError, setPostalLookupError] = useState('');
  const postalLookupRequestRef = useRef(0);
  const { t } = useTranslation();

  // 获取当前用户信息
  const currentUser: any = useSelector((state: any) => {
    return state?.User?.data?.[0] ?? {};
  });

  // 判断是否是管理员
  const isAdmin = currentUser?.userType === 4;
  // 判断是否是采购员
  const isPurchaser = currentUser?.userType === 5;
  // 关闭抽屉
  const onClose = (newItem: any) => {
    // closeDrawer && closeDrawer(selectConsignee);
    // setConsignee(newItem);
    closeDrawer && closeDrawer();
  };
  const onClose2 = (item: any) => {
    const newConsigneeList = consigneeList.map(value => {
      if (value.id === item.id) {
        // item.defaultStatus = 1;
        return { ...item, defaultStatus: 1 };
      } else {
        // item.defaultStatus = 0;
        return { ...value, defaultStatus: 0 };
      }
    });
    const newItem = { ...item };
    newItem.defaultStatus = 1;
    // setSelectConsignee(newItem);
    setConsignee(newItem);
    setConsigneeList(newConsigneeList);
    // closeDrawer && closeDrawer(selectConsignee);
    closeDrawer && closeDrawer();
  };
  const onClose3 = async (item: any) => {
    await Promise.resolve(setConsignee(item));
    closeDrawer && closeDrawer();
  };
  // 修改搜索词
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const searchWord = (v: any) => {
    const raw = (v?.target?.value ?? '').trim();
    if (!raw) {
      setCurrentSearchWord('');
      return;
    }
    setCurrentSearchWord(escapeRegExp(raw));
  };

  const handlePostalCodeSearch = async () => {
    if (postalLookupLoading) return;
    const normalizedPostalCode = postalCode.trim();
    if (!normalizedPostalCode) {
      setPostalLookupError(TLT('请输入邮政编码'));
      return;
    }

    const requestId = ++postalLookupRequestRef.current;
    setPostalLookupLoading(true);
    setPostalLookupError('');
    try {
      const res: any = await getPostalCodeAddress(normalizedPostalCode, 'location');
      if (requestId !== postalLookupRequestRef.current) return;
      if (res?.success === false) {
        const errorMessage = res.msg || TLT('邮政编码查询失败，请重试');
        setPostalLookupError(errorMessage);
        message.error(errorMessage);
        return;
      }

      // 国际地址服务可能返回多组匹配，产品约定固定采用第一组。
      const location = normalizePostalCodeAddress(res, normalizedPostalCode);
      if (!location) {
        const emptyMessage = TLT('未查询到该邮政编码对应的地址');
        setPostalLookupError(emptyMessage);
        message.warning(emptyMessage);
        return;
      }

      await Promise.resolve(onPostalLocationSelect?.(location));
      closeDrawer?.();
    } catch (error) {
      if (requestId !== postalLookupRequestRef.current) return;
      console.error('postal code location lookup failed', error);
      const errorMessage = TLT('邮政编码查询失败，请重试');
      setPostalLookupError(errorMessage);
      message.error(errorMessage);
    } finally {
      if (requestId === postalLookupRequestRef.current) {
        setPostalLookupLoading(false);
      }
    }
  };

  // 获取地址列表
  const fetchAddressList = async () => {
    setLoading(true);
    try {
      const params: GetAddressListType = {
        pageNo: 1,
        pageSize: 1000, // 获取所有地址
        keywords: '',
      };
      const res: any = await getAddressList(filterParams(params) as GetAddressListType);
      if (res.success) {
        const items = res.value.items || [];
        // 转换数据格式以匹配原有逻辑
        const formattedItems = items.map((item: any) => ({
          id: item.addressManagerId || item.id,
          addressId: item.addressManagerId || item.id,
          receiveName: item.receiverName || item.receiveName,
          receivePhone: item.receiverPhone || item.receivePhone,
          detailAddress: item.detailAddress,
          areaAddress:
            item.areaAddress ||
            `${item.countryName || ''} ${item.provinceName || ''} ${item.cityName || ''} ${item.countyName ||
              ''}`.trim(),
          postalCode: item.postalCode || '',
          defaultStatus: item.defaultStatus || 0,
          isSelf: item.isSelf,
          creator: item.creator,
          pin: item.pin,
          createPin: item.createPin,
          country: item.country,
          countryName: item.countryName,
          provinceName: item.provinceName,
          cityName: item.cityName,
          countyName: item.countyName,
          sortRule: item.sortRule,
        }));
        setAddressList(formattedItems);
        // 如果 consigneeList 为空，更新它
        if (!consigneeList || consigneeList.length === 0) {
          setConsigneeList(formattedItems);
        }
      }
    } catch (error) {
      console.error('获取地址列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 监听外层抽屉打开关闭
  useEffect(() => {
    postalLookupRequestRef.current += 1;
    setPostalLookupLoading(false);
    setOpen(showDrawer);
    if (showDrawer) {
      setPostalCode(initialPostalCode);
      setPostalLookupError('');
      // 打开抽屉时获取地址列表
      fetchAddressList();
    }
  }, [showDrawer]);

  const visibleAddressList = useMemo(() => {
    return (addressList.length > 0 ? addressList : consigneeList)?.filter(showlist => {
      if (!currentSearchWord) return true;
      const searchRegex = new RegExp(currentSearchWord, 'i');
      if (
        showlist.receiveName?.match(searchRegex) ||
        showlist.receivePhone?.match(searchRegex) ||
        showlist.areaAddress?.match(searchRegex) ||
        showlist.detailAddress?.match(searchRegex)
      ) {
        return true;
      }
      return false;
    });
  }, [addressList, consigneeList, currentSearchWord]);

  return (
    <div className={styles.drawerlistbox} id="AddressSelectorDrawer">
      <Drawer
        title={
          <div className={styles.drawertitle}>
            <div className={styles.title}>{`${TLT(`配送地址选择`)} (${consigneeList?.length})`}</div>
            <button type="button" className={styles.closeButton} onClick={onClose} aria-label={TLT('关闭')}>
              <img className={styles.closeIcon} src={closeIcon} alt="" />
            </button>
          </div>
        }
        placement="right"
        closable={false}
        getContainer={() => document.getElementById('AddressSelectorDrawer') as HTMLDivElement}
        onClose={onClose}
        open={open}
        width={800}
      >
        <div className={styles.drawerlist}>
          {showPostalCodeLookup && (
            <form
              className={styles.postalLookupForm}
              role="search"
              onSubmit={event => {
                event.preventDefault();
                void handlePostalCodeSearch();
              }}
            >
              <label className={styles.postalLookupLabel} htmlFor="header-postal-code">
                {TLT('邮政编码')}：
              </label>
              <div className={styles.postalLookupField}>
                <Input
                  id="header-postal-code"
                  value={postalCode}
                  maxLength={20}
                  disabled={postalLookupLoading}
                  status={postalLookupError ? 'error' : undefined}
                  aria-invalid={Boolean(postalLookupError)}
                  aria-describedby={postalLookupError ? 'header-postal-code-error' : undefined}
                  placeholder={TLT('请输入')}
                  onChange={event => {
                    setPostalCode(event.target.value);
                    if (postalLookupError) setPostalLookupError('');
                  }}
                />
                <div id="header-postal-code-error" className={styles.postalLookupError} aria-live="polite">
                  {postalLookupError}
                </div>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                loading={postalLookupLoading}
                className={styles.postalLookupButton}
                icon={<img className={styles.searchIcon} src={searchIcon} alt="" />}
              >
                {TLT('查询')}
              </Button>
            </form>
          )}
          <div className={styles.drawerlist_searchbox}>
            <Input
              onChange={searchWord}
              placeholder={TLT(`请输入要搜索的地址`)}
              prefix={<SearchOutlined className="site-form-item-icon" />}
              allowClear
            />
          </div>

          <div className={styles.drawerlist_listbox}>
            {loading ? (
              <div style={{ padding: '20px' }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </div>
            ) : (
              visibleAddressList?.map(item => {
                  // 判断是否是当前用户创建的地址
                  const isOwnAddress = (item.creator || item.pin || item.createPin) === currentUser.pin;
                  // 判断是否是管理员下发的地址（采购员视角）
                  const isAdminAddress = isPurchaser && item.isSelf === 2;
                  const isActive =
                    consignee &&
                    (item.id === consignee.id ||
                      item.addressId === consignee.id ||
                      item.addressId === consignee.addressId);

                  return (
                    <div
                      key={item.id || item.addressId}
                      onClick={() => {
                        onClose3(item);
                      }}
                      className={styles.addressCard}
                    >
                      <div className={`${styles.addressContent} ${isActive && styles.addressContentActive}`}>
                        <div className={styles.contactInfo}>
                          {/* 地址来源标签 */}
                          {isAdminAddress ? (
                            <Tag className={styles.sourceTagCommon}>
                              <TeamOutlined />
                              <span>{TLT(`管理员`)}</span>
                            </Tag>
                          ) : isOwnAddress && !isAdmin ? (
                            <Tag className={styles.sourceTagPersonal}>
                              <img className={styles.sourceTagIcon} src={roleIcon} alt="" />
                              <span>{TLT(`我的`)}</span>
                            </Tag>
                          ) : null}
                          {/* 默认地址标签 */}
                          {item.defaultStatus === 1 && (
                            <Tag className={styles.defaultTag}>
                              <img className={styles.defaultTagIcon} src={locationIcon} alt="" />
                              <span>{TLT(`默认`)}</span>
                            </Tag>
                          )}
                          <span className={styles.companyName}>{item.receiveName}</span>
                          <span className={styles.phoneNumber}>{item.receivePhone}</span>
                        </div>
                        <p className={styles.addressDetails}>
                          {item?.postalCode && (
                            <>
                              <span className={styles.postalCode}>{item.postalCode}</span>
                              <span className={styles.postalCodeSpacer}> </span>
                            </>
                          )}
                          <span className={styles.addressText}>
                            {item?.areaAddress ? item.areaAddress : item?.country}
                            {item?.detailAddress ? ` ${item.detailAddress}` : ''}
                          </span>
                        </p>

                        {isActive && (
                          <span className={styles.addressContentIcon} aria-hidden="true">
                            <img className={styles.selectIcon} src={selectIcon} alt="" />
                          </span>
                        )}
                      </div>

                      <div className={styles.line} />
                    </div>
                  );
                })
            )}
          </div>
          {!loading && showAddAddressButton && (
            <div className={styles.drawerFooter}>
              <Button
                type="primary"
                className={styles.addAddressButton}
                onClick={() => {
                  closeDrawer && closeDrawer();
                  onAddAddressClick && onAddAddressClick();
                }}
              >
                <span className={styles.addButtonContent}>
                  <img className={styles.addIcon} src={addIcon} alt="" />
                  <span>{TLT('添加地址')}</span>
                </span>
              </Button>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};
